import React, { useEffect } from "react";
import { trackEvent } from "../lib/api";

/**
 * Simplified layered bands — pink (brand story) then white (social).
 * Content is centered within each band.
 */
const PINK = "#FDE6EE";

const Band = ({ bg, testId, children }) => (
  <section
    data-testid={testId}
    className="w-full"
    style={{ backgroundColor: bg }}
  >
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-24 md:py-32 flex flex-col items-center text-center">
      {children}
    </div>
  </section>
);

const About = () => {
  useEffect(() => {
    trackEvent("pageview");
  }, []);

  return (
    <div className="w-full">
      {/* Band 1 — white, "How It Started" */}
      <Band bg="#FFFFFF" testId="about-band-origin">
        <h2
          data-testid="about-origin-title"
          className="font-serif-display font-black text-3xl md:text-4xl tracking-tight mb-6"
        >
          How It Started
        </h2>
        <p
          data-testid="about-origin-story"
          className="text-base md:text-[17px] leading-[1.7] text-black/75 max-w-2xl"
        >
          Briology was founded in 2026 at local Phoenix markets to introduce
          the community to the richness of concentrated, homemade perfume oils
          inspired by Middle Eastern and African fragrance traditions. Crafted
          with a hydrating Jojoba base, our oils are designed to nourish
          sensitive skin while delivering the exceptional longevity that
          alcohol-based sprays lack. What began as a local market popup has
          grown into a space where you can layer and customize your own
          long-lasting signature scent.
        </p>

        {/* Phoenix market polaroid — hand-placed feel */}
        <figure
          data-testid="phoenix-polaroid"
          className="mt-12 md:mt-14 relative bg-white pt-4 px-4 pb-14 max-w-[520px] w-full"
          style={{
            transform: "rotate(-2.4deg)",
            boxShadow:
              "0 26px 40px -14px rgba(60,40,20,0.32), 0 10px 18px -6px rgba(60,40,20,0.18), 0 1px 0 rgba(255,255,255,0.9) inset",
          }}
        >
          <img
            src="/assets/phoenix_market.jpg"
            alt="Briology · original Phoenix market setup"
            draggable={false}
            className="block w-full aspect-[3/2] bg-[#F4E5C9]"
            style={{ objectFit: "cover", objectPosition: "50% 50%" }}
          />
        </figure>
      </Band>

      {/* Band 2 — pink, brand story */}
      <Band bg={PINK} testId="about-band-story">
        <p
          data-testid="about-brand-story"
          className="font-serif-display italic text-lg md:text-2xl leading-[1.6] text-black/80 max-w-2xl"
        >
          Briology is a custom perfume oil brand where you curate your own
          signature scent. Choose from our collection of fine fragrances to
          mix a blend that is entirely your own. Formulated in a base of pure
          Jojoba Oil, our concentrated perfume oils are long-lasting, deeply
          hydrating, and gentle on sensitive skin&mdash;giving you a seamless,
          personal scent experience without harsh alcohols.
        </p>
      </Band>

      {/* Band 3 — white, social media */}
      <Band bg="#FFFFFF" testId="about-band-social">
        <div className="w-full max-w-md flex flex-col gap-3">
          <a
            data-testid="social-instagram"
            href="https://instagram.com/brio.logy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between border border-black p-4 hover:bg-[#FF1493] hover:text-white transition-colors group text-left"
          >
            <div>
              <div className="overline text-[10px] group-hover:text-white">
                Instagram
              </div>
              <div className="font-serif-display text-xl font-black">
                @brio.logy
              </div>
            </div>
            <span className="font-serif-display text-2xl">↗</span>
          </a>
          <a
            data-testid="social-email"
            href="mailto:Brio.logy@outlook.com"
            className="flex items-center justify-between border border-black p-4 hover:bg-black hover:text-white transition-colors group text-left"
          >
            <div>
              <div className="overline text-[10px] group-hover:text-white">
                Email
              </div>
              <div className="font-serif-display text-xl font-black break-all">
                Brio.logy@outlook.com
              </div>
            </div>
            <span className="font-serif-display text-2xl">↗</span>
          </a>
        </div>
      </Band>
    </div>
  );
};

export default About;
